"use client";

import { useCallback, useEffect, useState } from "react";

import { cn } from "@/lib/utils";

/**
 * Directional crossfade between the views of a single surface (a dialog that
 * swaps a form for a confirmation, a wizard step, …).
 *
 * The two views overlap: the incoming one mounts immediately and fades up
 * while the outgoing one — absolutely positioned on top of it, so it is out of
 * flow and never affects height — fades and slides away. Swapping them in
 * sequence instead leaves the surface empty for the length of the gap, which
 * on a light card reads as a blink rather than a transition.
 *
 * Pair `renderSwap` with a `SmoothHeight` wrapper so the surface's height
 * eases between the two instead of snapping.
 */

/** How long a swap takes end to end. */
export const VIEW_SWAP_MS = 300;

type ViewSwapDirection = "forward" | "back";

export type ViewSwap<TView extends string> = {
  /** The live view — updates as soon as `swapTo` is called. */
  view: TView;
  /** True while a swap is still animating. */
  isSwapping: boolean;
  /** Animate to `next`; "back" reverses which way the pair travels. */
  swapTo: (next: TView, direction?: ViewSwapDirection) => void;
  /** Jump to `next` with no motion (e.g. resetting a closed dialog). */
  resetTo: (next: TView) => void;
  /** Renders the current view, plus the outgoing one while it leaves. */
  renderSwap: (render: (view: TView) => React.ReactNode) => React.ReactNode;
};

export function useViewSwap<TView extends string>(
  initialView: TView | (() => TView),
): ViewSwap<TView> {
  const [view, setView] = useState<TView>(initialView);
  const [exitingView, setExitingView] = useState<TView | null>(null);
  const [direction, setDirection] = useState<ViewSwapDirection>("forward");
  // "start" holds both views at their pre-animation positions for a painted
  // frame; without it the browser has nothing to transition from.
  const [phase, setPhase] = useState<"idle" | "start" | "run">("idle");

  const swapTo = useCallback(
    (next: TView, nextDirection: ViewSwapDirection = "forward") => {
      if (next === view) return;

      setExitingView(view);
      setDirection(nextDirection);
      setView(next);
      setPhase("start");
    },
    [view],
  );

  const resetTo = useCallback((next: TView) => {
    setView(next);
    setExitingView(null);
    setDirection("forward");
    setPhase("idle");
  }, []);

  useEffect(() => {
    if (phase !== "start") return;

    let innerFrameId = 0;
    const outerFrameId = requestAnimationFrame(() => {
      innerFrameId = requestAnimationFrame(() => setPhase("run"));
    });

    return () => {
      cancelAnimationFrame(outerFrameId);
      cancelAnimationFrame(innerFrameId);
    };
  }, [phase]);

  useEffect(() => {
    if (phase !== "run") return;

    const timeout = window.setTimeout(() => {
      setExitingView(null);
      setPhase("idle");
    }, VIEW_SWAP_MS);

    return () => window.clearTimeout(timeout);
  }, [phase]);

  const offset = direction === "back" ? "translate-x-4" : "-translate-x-4";
  const enterOffset = direction === "back" ? "-translate-x-4" : "translate-x-4";

  const renderSwap = (render: (view: TView) => React.ReactNode) => (
    <div className="relative">
      <div
        className={cn(
          "transition-[opacity,transform] ease-out",
          phase === "start"
            ? cn("opacity-0 duration-0", enterOffset)
            : cn(
                "translate-x-0 opacity-100 duration-300",
                phase === "run" && "pointer-events-none",
              ),
        )}
      >
        {render(view)}
      </div>
      {exitingView !== null && (
        <div
          aria-hidden="true"
          // React 19 forwards this: keeps the outgoing copy out of the tab
          // order (and off assistive tech) for the frames it lingers.
          inert
          className={cn(
            "pointer-events-none absolute inset-x-0 top-0 transition-[opacity,transform] ease-out",
            phase === "start"
              ? "translate-x-0 opacity-100 duration-0"
              : cn("opacity-0 duration-200", offset),
          )}
        >
          {render(exitingView)}
        </div>
      )}
    </div>
  );

  return { view, isSwapping: phase !== "idle", swapTo, resetTo, renderSwap };
}
